import React from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Box,
  useTheme,
  Typography,
  Skeleton,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText
} from '@mui/material';
import { Inventory } from '@mui/icons-material';
import { useTopStorageUnits } from '../hooks/useDashboardData';

export const TopStorageUnits: React.FC = () => {
  const theme = useTheme();
  const { data, isLoading, error } = useTopStorageUnits(5);

  const getMedalColor = (index: number) => {
    switch (index) {
      case 0:
        return theme.colors.gradients.orange1;
      case 1:
        return theme.colors.alpha.black[30];
      case 2:
        return theme.colors.gradients.orange3;
      default:
        return theme.colors.primary.main;
    }
  };

  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: theme.general.borderRadius,
        boxShadow: theme.colors.shadows.card
      }}
    >
      <CardHeader
        title={
          <Typography variant="h4" fontWeight={700}>
            Top Storage Units
          </Typography>
        }
      />
      <CardContent sx={{ pt: 0 }}>
        {isLoading ? (
          <List>
            {Array.from(new Array(5)).map((_, index) => (
              <ListItem key={index} sx={{ px: 0 }}>
                <ListItemAvatar>
                  <Skeleton variant="circular" width={48} height={48} />
                </ListItemAvatar>
                <ListItemText
                  primary={<Skeleton width="80%" />}
                  secondary={<Skeleton width="60%" />}
                />
              </ListItem>
            ))}
          </List>
        ) : error ? (
          <Typography color="error">Failed to load data</Typography>
        ) : data && data.storageUnits.length > 0 ? (
          <List sx={{ py: 0 }}>
            {data.storageUnits.map((unit, index) => (
              <ListItem
                key={unit.storageUnitId}
                sx={{
                  px: 0,
                  py: 2,
                  borderBottom:
                    index < data.storageUnits.length - 1
                      ? `1px solid ${theme.palette.divider}`
                      : 'none'
                }}
              >
                <ListItemAvatar>
                  <Avatar
                    sx={{
                      width: 48,
                      height: 48,
                      background: getMedalColor(index),
                      fontWeight: 700,
                      fontSize: '1.125rem'
                    }}
                  >
                    {index < 3 ? (
                      index + 1
                    ) : (
                      <Inventory sx={{ fontSize: '1.5rem' }} />
                    )}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Typography variant="subtitle1" fontWeight={700}>
                      {unit.storageUnitName}
                    </Typography>
                  }
                  secondary={
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        {unit.storageUnitCode}
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 2,
                          mt: 0.5
                        }}
                      >
                        <Typography variant="caption" fontWeight={600}>
                          {unit.totalQuantity.toLocaleString()} units
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {unit.uniqueUnits} SKUs
                        </Typography>
                      </Box>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography color="text.secondary">No data available</Typography>
        )}
      </CardContent>
    </Card>
  );
};